import { ReactNode, RefObject } from "react";
import styled, { css } from "styled-components";
import { FloatingContext } from "@floating-ui/react";
import {
  elevation00,
  primary03,
  semanticRed02,
  shade0,
  shade300,
  shade700,
  shade800,
  shade900,
  shade950,
} from "../../../styles/tokens";
import { Body } from "../../body/Body";
import { normalStyles, xSmallStyles } from "../../body/Body.Parts";
import { IconButton } from "../../icon-button/IconButton.Parts";
import { Size } from "../../selection/Selection.Parts";
import {
  BORDER_WIDTH_PX,
  CHEVRON_GAP,
  Props as ControllerProps,
  DEFAULT_WIDTH,
  PADDING_LEFT,
  PADDING_RIGHT,
  ROW_GAP,
  ROW_HEIGHT,
  SMALL_PADDING_LEFT,
  SMALL_PADDING_RIGHT,
  SelectOption,
} from "../Select.Parts";

// TEXT

// CONSTANTS
const LABEL_HEIGHT = 1.25;
const MULTI_ROW_HEIGHT = 2.5;
const PADDING_Y = 0.4375;
const LABELLED_PADDING_Y = 0.3125;
const MULTI_ROW_PADDING_Y = 1;
const SMALL_PADDING_Y = 0.1875;

// STYLES
export const Button = styled.div<ButtonProps>`
  background: ${shade950};
  padding: ${(props) => getPadding(props)};
  cursor: ${({ $multi, $selection }) =>
    !$multi || !$selection ? "pointer" : "default"};
  display: flex;
  gap: ${CHEVRON_GAP}rem;
  flex-wrap: nowrap;
  height: ${(props) => calculateHeight(props)};

  width: ${({ $width, $maxWidth }) =>
    $maxWidth ? "max-content" : $width || `${DEFAULT_WIDTH}rem`};
  min-width: ${({ $maxWidth, $width }) =>
    $maxWidth ? $width || `${DEFAULT_WIDTH}rem` : `${DEFAULT_WIDTH}rem`};
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}

  ${normalStyles};

  ${({ $disabled, $hasError, $active, $selection, $subtle }) =>
    $disabled
      ? css`
          cursor: not-allowed;
          color: ${shade700};
          border: ${BORDER_WIDTH_PX}px solid
            ${$subtle ? "transparent" : shade800};
        `
      : css`
          border: ${BORDER_WIDTH_PX}px solid
            ${$hasError ? semanticRed02 : $subtle ? "transparent" : shade800};

          &:hover {
            box-shadow: ${elevation00};
            ${!$hasError && !$subtle && `border-color: ${shade700}`};
            ${$active &&
            !$subtle &&
            `border: ${BORDER_WIDTH_PX}px solid ${shade0};`}
            ${IconButton} {
              color: ${!$selection && primary03};
            }
            ${$subtle && `background-color: ${shade900};`};
          }

          &:focus-visible {
            border: ${BORDER_WIDTH_PX}px solid ${shade0};
            ${IconButton} {
              color: ${!$selection && primary03};
            }
          }

          ${$active &&
          !$subtle &&
          `border: ${BORDER_WIDTH_PX}px solid ${shade0};`}
        `}

  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;
`;

export const Content = styled.div<SelectionContainerProps>`
  display: flex;
  flex-direction: column;
  padding-top: ${({ $active }) => ($active ? "0" : "1rem")};
  align-items: ${({ $rows }) => ($rows && $rows > 1 ? "flex-start" : "center")};
  align-content: ${({ $multi }) => ($multi ? "flex-start" : "center")};

  flex: 1;
  min-width: 0;

  transition: padding-top 0.3s ease;
`;

export const LabelContainer = styled.div<LabelContainerProps>`
  display: flex;
  align-items: center;
  height: ${LABEL_HEIGHT}rem;
  width: 100%;
  color: ${shade300};

  label {
    transition:
      font-size 0.3s ease,
      line-height 0.3s ease;
    ${({ $smallLabel }) => $smallLabel && xSmallStyles}
  }
`;

export const Placeholder = styled(Body)`
  width: 100%;
  height: ${ROW_HEIGHT}rem;
  display: flex;
  align-items: center;
`;

export const ToggleButtonContainer = styled.div<ToggleButtonContainerProps>`
  height: 100%;
  display: flex;
  align-items: ${({ $rows }) => !$rows && "center"};
  padding-top: ${({ $rows }) => $rows && "1rem"};
`;

// DATA

// TYPES
export type Props = Omit<
  ControllerProps,
  "id" | "options" | "ref" | "defaultSelection" | "onChange" | "listMaxHeight"
> & {
  /** Button Element Props */
  id: string; // The unique ID assigned to the select trigger's button element

  /** Button Styling Props  */
  hasContent: boolean; // True if there is content to show inside the trigger (selection, placeholder, or search).
  subtle?: boolean; // Whether to use the subtle styling variant for the select trigger button.

  /** Floating UI Context */
  isOpen: boolean; // Boolean indicating if the floating dropdown/list is currently visible.
  handleOpenChange: (isOpen: boolean) => void; // Called to request opening or closing the dropdown (e.g., on click).
  context: FloatingContext; // The Floating UI context object for managing state and lifecycle.
  referenceProps: Record<string, unknown>; // Props that must be spread onto the trigger element for Floating UI functionality.
  inputRef: RefObject<HTMLInputElement | null>; // Ref object for the internal <input> element (if present).
  buttonRef: RefObject<HTMLDivElement | null>; // Ref object for the main button/control part of the trigger.
  setAnchorEl: (node: HTMLElement | null) => void; // Setter function to define the visual anchor point for the Floating UI positioning.
  referenceRef: (node: HTMLDivElement | null) => void; // Callback ref used by the trigger to register itself as the Floating UI reference.
  activeIndex: number | null; // The index of the item currently highlighted by keyboard navigation using virtual focus in the floating dropdown.

  /** Selection State & Data */
  filteredOptions: SelectOption[]; // The options remaining after applying the current search filter.
  getLabelForValue: (value: string | number) => string; // Utility function to convert a raw option value into its display label.
  handleSelect: (index: number) => void; // Handler called when an item is selected (part of the controller interface).
  setSelection: (newSelection: (string | number)[]) => void; // Function to update the current selection array.
  clearSelection: (e?: React.MouseEvent) => void; // Handler called when the user clicks a button to clear all selections.
  clearable: boolean; // Whether to allow clearing the selection (only applicable for single select).

  /** Search Input */
  searchValue: string; // The current text typed into the search input field.
  searchInputId: string; // Unique ID assigned to the search input element for accessibility (`aria-labelledby`).
  searchInput: ReactNode; // The search input component/element to be rendered inside the trigger.

  /** Selection Pills and their Visibility Logic in the Button */
  selection: (string | number)[]; // The entire array of currently selected values.
  visibleSelections: (string | number)[]; // The subset of selected values that are visible within the trigger's space.
  overflowCount: number; // The count of selected items not displayed due to space constraints (e.g., '+3 more').
};

type ButtonProps = {
  $label?: string;
  $multi?: boolean;
  $selection?: boolean;
  $active?: boolean;
  $disabled?: boolean;
  $hasError?: boolean;
  $width?: string;
  $maxWidth?: string | number;
  $rows?: number;
  $subtle?: boolean;
  $small?: boolean;
};

export type SelectionContainerProps = {
  $rows: number;
  $multi?: boolean;
  $active?: boolean;
};

type ToggleButtonContainerProps = {
  $rows?: boolean;
};

type LabelContainerProps = {
  $smallLabel?: boolean;
};

// FUNCTIONS
export const getSelectionSize = (rows: number, small?: boolean): Size =>
  rows > 1 && !small ? "medium" : "small";

export const getRowHeight = (rows: number, small?: boolean): number =>
  getSelectionSize(rows, small) === "medium" ? MULTI_ROW_HEIGHT : ROW_HEIGHT;

const getPaddingY = ({ $small, $rows = 1, $label }: ButtonProps): number => {
  if ($small) return SMALL_PADDING_Y;
  if ($rows > 1) return MULTI_ROW_PADDING_Y;
  return $label ? LABELLED_PADDING_Y : PADDING_Y;
};

const getPadding = (props: ButtonProps): string => {
  const y = getPaddingY(props);
  const right = props.$small ? SMALL_PADDING_RIGHT : PADDING_RIGHT;
  const left = props.$small ? SMALL_PADDING_LEFT : PADDING_LEFT;

  return `${y}rem ${right}rem ${y}rem ${left}rem`;
};

const calculateHeight = (props: ButtonProps): string => {
  const { $rows = 1, $label, $small } = props;
  const content =
    2 * getPaddingY(props) +
    ($label ? LABEL_HEIGHT : 0) +
    $rows * getRowHeight($rows, $small) +
    ($rows - 1) * ROW_GAP;

  return `calc(${content}rem + ${2 * BORDER_WIDTH_PX}px)`;
};

// ASSETS
